/**
 * Room Detail Modal
 * Shows all versions of a room image with actions + version history.
 * Regeneration/editing is handled by the parent via RegenerationModal
 * (same component used in the staging tool) — no inline regen UI here.
 */

import { useState, useCallback, useEffect } from 'react';
import {
  X,
  Download,
  Trash2,
  Calendar,
  Tag,
  Image as ImageIcon,
  Loader2,
  Check,
  Wand2,
  History,
  RotateCcw,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { GalleryImage, ImageHistoryEntry } from '@/types';
import { saveAs } from 'file-saver';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';

type ImageVersion = 'original' | 'empty' | 'staged' | 'upscaled_staged' | 'upscaled_empty' | 'branded' | 'branded_empty';

interface RoomDetailModalProps {
  image: GalleryImage;
  projectId: string;
  onClose: () => void;
  onDeleteVersion: (imageId: string, version: ImageVersion, storagePath: string) => Promise<void>;
  /** Direct re-roll (no prompt) */
  onRegenerate: (version: 'empty' | 'staged') => void;
  /** Open feedback/edit flow in parent */
  onEdit: (version: 'empty' | 'staged', storagePath?: string, currentUrl?: string) => void;
  /** True while parent is running a generation — shows overlay + disables actions */
  isRegenerating?: boolean;
  onRestoreVersion?: (imageId: string, versionType: 'Empty' | 'Staged' | 'Floorplan2D' | 'Floorplan3D', storagePath: string) => Promise<void>;
}

export const RoomDetailModal: React.FC<RoomDetailModalProps> = ({
  image,
  projectId: _projectId,
  onClose,
  onDeleteVersion,
  onRegenerate,
  onEdit,
  isRegenerating = false,
  onRestoreVersion,
}) => {
  const [selectedVersion, setSelectedVersion] = useState<ImageVersion>(() => {
    if (image.brandedUrl) return 'branded';
    if (image.brandedEmptyUrl) return 'branded_empty';
    if (image.upscaledStagedUrl) return 'upscaled_staged';
    if (image.upscaledEmptyUrl) return 'upscaled_empty';
    if (image.stagedUrl) return 'staged';
    if (image.emptyUrl) return 'empty';
    return 'original';
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // History state
  const [historyEntries, setHistoryEntries] = useState<ImageHistoryEntry[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [previewHistoryEntry, setPreviewHistoryEntry] = useState<ImageHistoryEntry | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);

  const getPublicUrl = useCallback((storagePath: string) => {
    const { data } = supabase.storage.from('images').getPublicUrl(storagePath);
    return data.publicUrl;
  }, []);

  // Load history when viewing staged/empty
  useEffect(() => {
    const loadHistory = async () => {
      if (selectedVersion !== 'empty' && selectedVersion !== 'staged') {
        setHistoryEntries([]);
        return;
      }
      setIsLoadingHistory(true);
      try {
        const versionType = selectedVersion === 'empty' ? 'Empty' : 'Staged';
        const { data, error } = await supabase
          .from('image_history')
          .select('*')
          .eq('image_id', image.id)
          .in('version_type', [versionType, 'refinement'])
          .order('created_at', { ascending: false });
        if (error) {
          console.error('Failed to load history:', error);
          setHistoryEntries([]);
        } else {
          setHistoryEntries(data || []);
        }
      } catch (err) {
        console.error('Error loading history:', err);
        setHistoryEntries([]);
      } finally {
        setIsLoadingHistory(false);
      }
    };
    loadHistory();
    setPreviewHistoryEntry(null);
  }, [image.id, selectedVersion, image.emptyUrl, image.stagedUrl]);

  const handleRestoreVersion = useCallback(async () => {
    if (!previewHistoryEntry || !onRestoreVersion) return;
    setIsRestoring(true);
    try {
      await onRestoreVersion(image.id, previewHistoryEntry.version_type, previewHistoryEntry.storage_path);
      setPreviewHistoryEntry(null);
    } catch (error) {
      console.error('Restore failed:', error);
    } finally {
      setIsRestoring(false);
    }
  }, [previewHistoryEntry, image.id, onRestoreVersion]);

  const getCurrentUrl = useCallback((): string | undefined => {
    switch (selectedVersion) {
      case 'branded': return image.brandedUrl;
      case 'branded_empty': return image.brandedEmptyUrl;
      case 'upscaled_staged': return image.upscaledStagedUrl;
      case 'upscaled_empty': return image.upscaledEmptyUrl;
      case 'staged': return image.stagedUrl;
      case 'empty': return image.emptyUrl;
      case 'original': return image.originalUrl;
      default: return undefined;
    }
  }, [selectedVersion, image]);

  const currentUrl = getCurrentUrl();

  const getStoragePath = useCallback((): string => {
    const url = getCurrentUrl();
    if (!url) return '';
    const match = url.match(/\/images\/(.+)$/);
    return match ? match[1] : '';
  }, [getCurrentUrl]);

  const handleDownload = useCallback(async () => {
    if (!currentUrl) return;
    try {
      const response = await fetch(currentUrl);
      const blob = await response.blob();
      const filename = `${image.customLabel || image.roomType}_${selectedVersion}.png`;
      saveAs(blob, filename);
    } catch (error) {
      console.error('Download failed:', error);
    }
  }, [currentUrl, image, selectedVersion]);

  const handleDelete = useCallback(async () => {
    if (!currentUrl) return;
    setIsDeleting(true);
    try {
      const storagePath = getStoragePath();
      await onDeleteVersion(image.id, selectedVersion, storagePath);
      const remainingVersions = allVersions.filter(v => v.url && v.key !== selectedVersion);
      if (remainingVersions.length > 0) {
        setSelectedVersion(remainingVersions[0].key);
      } else {
        onClose();
      }
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  }, [currentUrl, getStoragePath, image.id, selectedVersion, onDeleteVersion, onClose]);

  const allVersions: { key: ImageVersion; label: string; url?: string; color: string }[] = [
    { key: 'original', label: 'Original', url: image.originalUrl, color: 'bg-slate-500' },
    { key: 'empty', label: 'Leer', url: image.emptyUrl, color: 'bg-blue-500' },
    { key: 'staged', label: 'Eingerichtet', url: image.stagedUrl, color: 'bg-green-500' },
    { key: 'upscaled_empty', label: 'Hochsk. Leer', url: image.upscaledEmptyUrl, color: 'bg-teal-500' },
    { key: 'upscaled_staged', label: 'Hochskaliert', url: image.upscaledStagedUrl, color: 'bg-orange-500' },
    { key: 'branded_empty', label: 'Gebr. Leer', url: image.brandedEmptyUrl, color: 'bg-cyan-500' },
    { key: 'branded', label: 'Gebrandet', url: image.brandedUrl, color: 'bg-purple-500' },
  ];
  const versions = allVersions.filter(v => v.url);

  const canRegenerate = selectedVersion === 'staged' || selectedVersion === 'empty';
  const canDelete = selectedVersion !== 'original';
  const roomLabel = image.customLabel || image.roomType;

  // Storage path and URL for the currently-viewed generated version
  const regenStoragePath = selectedVersion === 'staged' ? image.stagedStoragePath : image.emptyStoragePath;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden animate-fade-in"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-bold text-slate-900">{roomLabel}</h2>
            <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(image.createdAt).toLocaleDateString('de-DE', {
                  day: 'numeric', month: 'long', year: 'numeric',
                })}
              </span>
              <span className="flex items-center gap-1">
                <Tag className="w-4 h-4" />
                {versions.length} Versionen
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isRegenerating}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="flex flex-col lg:flex-row overflow-y-auto max-h-[calc(90vh-73px)]">
          {/* Main Image + History */}
          <div className="flex-1 p-4 bg-slate-50 flex flex-col min-w-0">
            {/* Main Image */}
            <div className="relative aspect-[4/3] max-h-[50vh] mx-auto rounded-xl overflow-hidden bg-slate-200 shadow-inner w-full">
              {isRegenerating && currentUrl ? (
                /* Generation overlay — same pattern as staging tool */
                <>
                  <img src={currentUrl} alt={roomLabel} className="w-full h-full object-contain opacity-40" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                    <div className="w-14 h-14 rounded-full bg-white/90 shadow-lg flex items-center justify-center">
                      <Loader2 className="w-7 h-7 text-primary animate-spin" />
                    </div>
                    <span className="text-xs font-semibold text-white drop-shadow bg-black/40 px-3 py-1 rounded-full">
                      KI generiert…
                    </span>
                  </div>
                </>
              ) : isRegenerating ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-muted gap-3">
                  <div className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-primary animate-pulse" />
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">KI-Generierung…</span>
                </div>
              ) : previewHistoryEntry ? (
                <img
                  src={getPublicUrl(previewHistoryEntry.storage_path)}
                  alt="Historische Version"
                  className="w-full h-full object-contain"
                />
              ) : currentUrl ? (
                <img
                  src={currentUrl}
                  alt={`${roomLabel} - ${selectedVersion}`}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="w-16 h-16 text-slate-400" />
                </div>
              )}

              {/* Version badge */}
              {!isRegenerating && (
                <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-white text-sm font-medium ${
                  previewHistoryEntry
                    ? 'bg-amber-500'
                    : versions.find(v => v.key === selectedVersion)?.color || 'bg-slate-500'
                }`}>
                  {previewHistoryEntry
                    ? `Verlauf: ${new Date(previewHistoryEntry.created_at).toLocaleString('de-DE', {
                        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                      })}`
                    : versions.find(v => v.key === selectedVersion)?.label}
                </div>
              )}

              {previewHistoryEntry && !isRegenerating && (
                <button
                  onClick={() => setPreviewHistoryEntry(null)}
                  className="absolute top-4 right-4 px-3 py-1 bg-slate-900/70 hover:bg-slate-900 text-white text-sm font-medium rounded-full transition-colors"
                >
                  ✕ Vorschau beenden
                </button>
              )}
            </div>

            {/* Restore button */}
            {previewHistoryEntry && onRestoreVersion && !isRegenerating && (
              <div className="mt-4 flex justify-center">
                <button
                  onClick={handleRestoreVersion}
                  disabled={isRestoring}
                  className="bg-amber-500 hover:bg-amber-600 text-white font-semibold py-2.5 px-6 rounded-xl transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {isRestoring ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Wird wiederhergestellt…</>
                  ) : (
                    <><RotateCcw className="w-4 h-4" /> Diese Version wiederherstellen</>
                  )}
                </button>
              </div>
            )}

            {/* History strip */}
            {(selectedVersion === 'empty' || selectedVersion === 'staged') && historyEntries.length > 0 && !isRegenerating && (
              <div className="mt-4 border-t border-slate-200 pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <History className="w-4 h-4 text-slate-500" />
                  <span className="text-sm font-semibold text-slate-700">
                    Verlauf ({historyEntries.length} {historyEntries.length === 1 ? 'Version' : 'Versionen'})
                  </span>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {historyEntries.map((entry, index) => (
                    <button
                      key={entry.id}
                      onClick={() => setPreviewHistoryEntry(
                        previewHistoryEntry?.id === entry.id ? null : entry
                      )}
                      className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        previewHistoryEntry?.id === entry.id
                          ? 'border-amber-500 ring-2 ring-amber-200'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <img
                        src={getPublicUrl(entry.storage_path)}
                        alt={`Version ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5 text-[10px] text-white text-center">
                        {new Date(entry.created_at).toLocaleDateString('de-DE', {
                          day: '2-digit', month: '2-digit',
                        })}
                      </div>
                      {previewHistoryEntry?.id === entry.id && (
                        <div className="absolute top-1 right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center">
                          <Check className="w-2.5 h-2.5 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {isLoadingHistory && (selectedVersion === 'empty' || selectedVersion === 'staged') && (
              <div className="mt-4 flex items-center justify-center gap-2 text-slate-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Lade Verlauf…
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="w-full lg:w-72 border-t lg:border-t-0 lg:border-l border-slate-200 flex flex-col flex-shrink-0">
            {/* Version selector */}
            <div className="p-4 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Versionen</h3>
              <div className="grid grid-cols-2 gap-2">
                {versions.map((version) => (
                  <button
                    key={version.key}
                    onClick={() => setSelectedVersion(version.key)}
                    disabled={isRegenerating}
                    className={`relative aspect-[4/3] rounded-lg overflow-hidden border-2 transition-all disabled:opacity-50 ${
                      selectedVersion === version.key
                        ? 'border-primary ring-2 ring-primary/20'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {version.url && (
                      <img src={version.url} alt={version.label} className="w-full h-full object-cover" />
                    )}
                    <div className={`absolute bottom-0 left-0 right-0 px-2 py-1 text-xs text-white font-medium ${version.color}`}>
                      {version.label}
                    </div>
                    {selectedVersion === version.key && (
                      <div className="absolute top-1 right-1 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="p-4 space-y-3 flex-1">
              <h3 className="text-sm font-semibold text-slate-700">Aktionen</h3>

              {/* Download */}
              <Button
                onClick={handleDownload}
                disabled={!currentUrl || isRegenerating}
                className="w-full gap-2"
              >
                <Download className="w-4 h-4" />
                Bild herunterladen
              </Button>

              {/* Regenerate / Edit — only for staged / empty versions */}
              {canRegenerate && (
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    onClick={() => onRegenerate(selectedVersion as 'empty' | 'staged')}
                    disabled={isRegenerating}
                    className="gap-1.5 text-sm font-medium"
                    title="Neu generieren — gleicher Prompt, neues Ergebnis"
                  >
                    {isRegenerating ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    {isRegenerating ? 'Läuft…' : 'Neu'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => onEdit(
                      selectedVersion as 'empty' | 'staged',
                      regenStoragePath,
                      currentUrl,
                    )}
                    disabled={isRegenerating}
                    className="gap-1.5 text-sm font-medium border-amber-300 text-amber-700 hover:bg-amber-50 hover:border-amber-400"
                    title="Bearbeiten — verfeinere mit eigenen Anweisungen"
                  >
                    <Wand2 className="w-4 h-4" />
                    Bearbeiten
                  </Button>
                </div>
              )}

              {/* Delete */}
              {canDelete && !showDeleteConfirm && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isRegenerating}
                  className="w-full text-red-600 hover:bg-red-50 border border-red-200 font-semibold py-2.5 px-4 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Version löschen
                </button>
              )}

              {showDeleteConfirm && (
                <div className="bg-red-50 rounded-xl p-4 space-y-3">
                  <p className="text-sm text-red-700">
                    Diese Version wirklich löschen? Das kann nicht rückgängig gemacht werden.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isDeleting}
                      className="flex-1 btn-ghost text-sm py-2"
                    >
                      Abbrechen
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      Löschen
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
